// 📄 useAvisoExpedicion.ts
// DEV es la fuente de verdad de parámetros/OT/Personalización Z para los
// pasos que SÍ requieren Orden de Transporte — QAS y PRD heredan esos
// valores en modo lectura y solo registran la confirmación de transporte
// (ver TransportConfirmation). Para los pasos que NO requieren OT, QAS y
// PRD mantienen su propia copia editable e independiente de los parámetros.
import { useState, useEffect } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { saveAsnConfig, saveTransportConfirmation, saveManualConfigDate, getGeneralsConfig, AsnFormData } from '../../../../services/commerce/integrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

const ASN_STEP_IDS = ['asn_idoc', 'asn_process', 'asn_mapping', 'asn_delivery', 'asn_test'];

const STEP_KEY_MAP: Record<string, string> = {
  asn_idoc: 'step_idoc', asn_process: 'step_process', asn_mapping: 'step_mapping',
  asn_delivery: 'step_delivery', asn_test: 'step_test'
};

const DEFAULT_FORM: AsnFormData = {
  // Paso 1
  partnerNumber: '', partnerType: 'LS', inboundPort: '',
  // Paso 2
  processCode: 'DEDI', functionModule: 'IDOC_INPUT_DESADV', autoDeliveryTrigger: true,
  // Paso 3
  cigProjectId: '', mapShipNoticeId: true, mapPackaging: false, mapBatch: false, mapPoReference: true,
  // Paso 4
  deliveryType: 'EL', referencePlant: '', warehouseNumber: '', autoDeliveryCreation: true, adoptionProfile: '',
  // Paso 5
  testShipNoticeId: '', generatedDeliveryDoc: '', idocMonitorStatus: 'Pendiente',
  // Comunes
  requiresOT: false, otNumber: '', comments: '',
};

export function useAvisoExpedicion() {
  const { projectId } = useProject();
  const [activeStep, setActiveStep] = useState('asn_idoc');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  const [rawConfig, setRawConfig] = useState<any>(null);

  const [formData, setFormData] = useState<AsnFormData>({ ...DEFAULT_FORM });

  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const handleFieldChange = (field: keyof AsnFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // OT/customDev/transport siempre se leen de DEV (son atributos del paso).
  // Los campos del formulario, en cambio, se leen de DEV solo si el paso
  // requiere OT — si no la requiere, QAS/PRD leen su propia copia.
  const loadEnvironmentData = (configSource = rawConfig) => {
    const devData = configSource?.environments?.DEV;
    if (!devData) {
      setFormData({ ...DEFAULT_FORM });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    if (devData.checklistProgress) {
      const completed = Object.keys(devData.checklistProgress).filter(
        key => devData.checklistProgress[key] === true && ASN_STEP_IDS.includes(key)
      );
      setCompletedSteps(completed);
    } else {
      setCompletedSteps([]);
    }

    const initialFormData: Partial<AsnFormData> = {};
    const asn = devData.channels?.advancedShippingNotice;
    const envChannelData = selectedEnv !== 'DEV' ? configSource?.environments?.[selectedEnv]?.channels?.advancedShippingNotice : null;
    let currentStepComments = '';
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};

    if (asn) {
      if (asn.step_idoc) {
        if (activeStep === 'asn_idoc') {
          const requiresOT = asn.step_idoc.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? asn.step_idoc : (envChannelData?.step_idoc || {});
          initialFormData.partnerNumber = source.partnerNumber || '';
          initialFormData.partnerType   = source.partnerType   || 'LS';
          initialFormData.inboundPort   = source.inboundPort   || '';
          currentStepComments           = source.comments      || '';
        }
        setRequiresOTMap(prev => ({ ...prev, asn_idoc: asn.step_idoc.requiresOT || false }));
        setOtNumberMap(prev  => ({ ...prev, asn_idoc: asn.step_idoc.otNumber    || '' }));
        setCustomDevMap(prev => ({ ...prev, asn_idoc: asn.step_idoc.customDevelopment || emptyCustomDevelopment() }));
        qasMap.asn_idoc = asn.step_idoc.transport?.QAS || emptyTransportConfirmation();
        prdMap.asn_idoc = asn.step_idoc.transport?.PRD || emptyTransportConfirmation();
      }
      if (asn.step_process) {
        if (activeStep === 'asn_process') {
          const requiresOT = asn.step_process.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? asn.step_process : (envChannelData?.step_process || {});
          initialFormData.processCode          = source.processCode          || 'DEDI';
          initialFormData.functionModule       = source.functionModule       || 'IDOC_INPUT_DESADV';
          initialFormData.autoDeliveryTrigger  = source.autoDeliveryTrigger  ?? true;
          currentStepComments                  = source.comments             || '';
        }
        setRequiresOTMap(prev => ({ ...prev, asn_process: asn.step_process.requiresOT || false }));
        setOtNumberMap(prev  => ({ ...prev, asn_process: asn.step_process.otNumber    || '' }));
        setCustomDevMap(prev => ({ ...prev, asn_process: asn.step_process.customDevelopment || emptyCustomDevelopment() }));
        qasMap.asn_process = asn.step_process.transport?.QAS || emptyTransportConfirmation();
        prdMap.asn_process = asn.step_process.transport?.PRD || emptyTransportConfirmation();
      }
      if (asn.step_mapping) {
        if (activeStep === 'asn_mapping') {
          const requiresOT = asn.step_mapping.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? asn.step_mapping : (envChannelData?.step_mapping || {});
          initialFormData.cigProjectId   = source.cigProjectId   || '';
          initialFormData.mapShipNoticeId= source.mapShipNoticeId ?? true;
          initialFormData.mapPackaging   = source.mapPackaging   || false;
          initialFormData.mapBatch       = source.mapBatch       || false;
          initialFormData.mapPoReference = source.mapPoReference ?? true;
          currentStepComments            = source.comments       || '';
        }
        setRequiresOTMap(prev => ({ ...prev, asn_mapping: asn.step_mapping.requiresOT || false }));
        setOtNumberMap(prev  => ({ ...prev, asn_mapping: asn.step_mapping.otNumber    || '' }));
        setCustomDevMap(prev => ({ ...prev, asn_mapping: asn.step_mapping.customDevelopment || emptyCustomDevelopment() }));
        qasMap.asn_mapping = asn.step_mapping.transport?.QAS || emptyTransportConfirmation();
        prdMap.asn_mapping = asn.step_mapping.transport?.PRD || emptyTransportConfirmation();
      }
      if (asn.step_delivery) {
        if (activeStep === 'asn_delivery') {
          const requiresOT = asn.step_delivery.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? asn.step_delivery : (envChannelData?.step_delivery || {});
          initialFormData.deliveryType          = source.deliveryType          || 'EL';
          initialFormData.referencePlant        = source.referencePlant        || '';
          initialFormData.warehouseNumber       = source.warehouseNumber       || '';
          initialFormData.autoDeliveryCreation  = source.autoDeliveryCreation  ?? true;
          initialFormData.adoptionProfile       = source.adoptionProfile       || '';
          currentStepComments                   = source.comments              || '';
        }
        setRequiresOTMap(prev => ({ ...prev, asn_delivery: asn.step_delivery.requiresOT || false }));
        setOtNumberMap(prev  => ({ ...prev, asn_delivery: asn.step_delivery.otNumber    || '' }));
        setCustomDevMap(prev => ({ ...prev, asn_delivery: asn.step_delivery.customDevelopment || emptyCustomDevelopment() }));
        qasMap.asn_delivery = asn.step_delivery.transport?.QAS || emptyTransportConfirmation();
        prdMap.asn_delivery = asn.step_delivery.transport?.PRD || emptyTransportConfirmation();
      }
      if (asn.step_test) {
        if (activeStep === 'asn_test') {
          const requiresOT = asn.step_test.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? asn.step_test : (envChannelData?.step_test || {});
          initialFormData.testShipNoticeId   = source.testShipNoticeId   || '';
          initialFormData.generatedDeliveryDoc= source.generatedDeliveryDoc|| '';
          initialFormData.idocMonitorStatus  = source.idocMonitorStatus  || 'Pendiente';
          currentStepComments                = source.comments           || '';
        }
        setRequiresOTMap(prev => ({ ...prev, asn_test: asn.step_test.requiresOT || false }));
        setOtNumberMap(prev  => ({ ...prev, asn_test: asn.step_test.otNumber    || '' }));
        setCustomDevMap(prev => ({ ...prev, asn_test: asn.step_test.customDevelopment || emptyCustomDevelopment() }));
        qasMap.asn_test = asn.step_test.transport?.QAS || emptyTransportConfirmation();
        prdMap.asn_test = asn.step_test.transport?.PRD || emptyTransportConfirmation();
      }
    }

    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = configSource?.environments?.QAS?.channels?.advancedShippingNotice;
    const prdChannelData = configSource?.environments?.PRD?.channels?.advancedShippingNotice;
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    ASN_STEP_IDS.forEach((id) => {
      const stepKey = STEP_KEY_MAP[id];
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    initialFormData.comments = currentStepComments;
    setFormData(prev => ({ ...prev, ...initialFormData }));
  };

  // Recargar datos del paso activo cuando cambia el step o el ambiente
  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, selectedEnv]);

  // Carga inicial desde Firestore al montar
  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error("Error cargando configuración de ASN:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);
    setSaveStatus('idle');

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: 'DEV' | 'QAS' | 'PRD' = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !formData.otNumber.trim()) {
      setValidationError("Ingresa el número de la Orden de Transporte."); return;
    }

    // Validaciones obligatorias por paso
    if (activeStep === 'asn_idoc' && (!formData.partnerNumber.trim() || !formData.inboundPort.trim())) {
      setValidationError("El número de interlocutor y el puerto de entrada son obligatorios."); return;
    }
    if (activeStep === 'asn_process' && (!formData.processCode.trim() || !formData.functionModule.trim())) {
      setValidationError("El código de proceso y el módulo de función son obligatorios."); return;
    }
    if (activeStep === 'asn_mapping' && !formData.cigProjectId.trim()) {
      setValidationError("El ID de proyecto CIG es obligatorio."); return;
    }
    if (activeStep === 'asn_delivery' && (!formData.deliveryType.trim() || !formData.referencePlant.trim())) {
      setValidationError("El tipo de entrega y el centro de referencia son obligatorios."); return;
    }
    if (activeStep === 'asn_test' && !formData.testShipNoticeId.trim()) {
      setValidationError("El ShipNotice ID de prueba es obligatorio."); return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");

      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await saveAsnConfig(projectId, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDate(projectId, 'advancedShippingNotice', STEP_KEY_MAP[activeStep], targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      // Actualizar rawConfig local para reflejar el paso completado
      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};
      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      updatedRaw.environments[targetEnv].checklistProgress[activeStep] = true;

      setRawConfig(updatedRaw);
      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`); return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      const stepKey = STEP_KEY_MAP[activeStep];
      await saveTransportConfirmation(projectId, 'advancedShippingNotice', stepKey, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeStep, setActiveStep, completedSteps, formData, handleFieldChange,
    requiresOTMap, setRequiresOTMap, otNumberMap, setOtNumberMap,
    customDevMap, setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError, setValidationError, saveStatus, loadingData,
    handleSave, selectedEnv, handleEnvChange,
  };
}
